// frontend/src/utils/csv.ts
// Generic CSV export: build a CSV string from headers + rows, trigger a browser download.
// RFC-4180-ish quoting: wraps values containing commas, newlines, or quotes;
// escapes embedded quotes by doubling them.

type CsvValue = string | number | null | undefined;

/**
 * Download a CSV file in the browser.
 *
 * @param filename - Suggested filename; ".csv" is appended if missing.
 * @param headers - Column headers (first row of the CSV).
 * @param rows - Data rows; each row is an array of values in the same order as headers.
 *
 * @example
 *   exportToCsv("paycheques_2026-10-04", ["Date", "Name", "Net"], [
 *     ["2026-10-01", "Jane Doe", 1234.56],
 *     ["2026-10-01", "John Smith", 987.65],
 *   ]);
 */
export function exportToCsv(
  filename: string,
  headers: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<CsvValue>>
): void {
  const safeName = filename.toLowerCase().endsWith(".csv")
    ? filename
    : filename + ".csv";

  const csv = [headers.map(escape), ...rows.map((row) => row.map(escape))]
    .map((row) => row.join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escape(v: CsvValue): string {
  const s = v == null ? "" : String(v);
  if (s.indexOf(",") >= 0 || s.indexOf("\n") >= 0 || s.indexOf('"') >= 0) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}